import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ForceGraph2D } from 'react-force-graph';
import { Link } from 'react-router-dom';
import styles from './KnowledgeGraph.module.css';
import Navigation from '../../components/Navigation/Navigation';
import { useNavigate } from 'react-router-dom';

const KnowledgeGraph = () => {
    const navigate = useNavigate();

    const isMobile = window.innerWidth <= 768;

    const [graphData, setGraphData] = useState({ nodes: [], links: [] });
    const [allNodes, setAllNodes] = useState([]);
    const [viewMode, setViewMode] = useState(isMobile ? 'list' : 'graph'); // 'graph' | 'list'

    // When enabled, we render labels, but only for "important" nodes (selected, hovered, search matches, neighbors).
    // Rendering every label makes the graph unusable on mobile.
    const [showLabels] = useState(true);

    const [searchQuery, setSearchQuery] = useState('');
    const [selectedNode, setSelectedNode] = useState(null);
    const [hoverNode, setHoverNode] = useState(null);

    const fgRef = useRef(null);
    const lastTapRef = useRef({ nodeId: null, atMs: 0 });

    useEffect(() => {
        fetch('/obsidianGraph.json')
            .then(response => response.json())
            .then(data => {
                // All nodes for the list view
                const sorted = [...data.nodes].sort((a, b) =>
                    (a.label || a.id).localeCompare(b.label || b.id)
                );
                setAllNodes(sorted);

                // Filter orphan nodes from the graph view
                const linkedIds = new Set();
                for (const l of data.links) {
                    linkedIds.add(typeof l.source === 'object' ? l.source.id : l.source);
                    linkedIds.add(typeof l.target === 'object' ? l.target.id : l.target);
                }
                setGraphData({
                    nodes: data.nodes.filter(n => linkedIds.has(n.id)),
                    links: data.links,
                });
            })
            .catch(error => {
                console.error('Error fetching graph data:', error);
            });
    }, []);

    const adjacency = useMemo(() => {
        // Map nodeId -> Set(neighborIds)
        const map = new Map();
        for (const n of graphData.nodes) map.set(n.id, new Set());
        for (const l of graphData.links) {
            const s = typeof l.source === 'object' ? l.source.id : l.source;
            const t = typeof l.target === 'object' ? l.target.id : l.target;
            if (!map.has(s)) map.set(s, new Set());
            if (!map.has(t)) map.set(t, new Set());
            map.get(s).add(t);
            map.get(t).add(s);
        }
        return map;
    }, [graphData.nodes, graphData.links]);

    const searchMatches = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        if (!q) return [];
        return graphData.nodes.filter(n => (n.label || '').toLowerCase().includes(q));
    }, [searchQuery, graphData.nodes]);

    const highlightedNodeIds = useMemo(() => {
        const ids = new Set();

        // Search matches
        for (const n of searchMatches) ids.add(n.id);

        // Selected + 1-hop neighborhood
        if (selectedNode?.id) {
            ids.add(selectedNode.id);
            const neigh = adjacency.get(selectedNode.id);
            if (neigh) for (const id of neigh) ids.add(id);
        }

        // Hovered
        if (hoverNode?.id) ids.add(hoverNode.id);

        return ids;
    }, [searchMatches, selectedNode, hoverNode, adjacency]);

    const nodeDegree = useMemo(() => {
        const deg = new Map();
        for (const n of graphData.nodes) deg.set(n.id, 0);
        for (const l of graphData.links) {
            const s = typeof l.source === 'object' ? l.source.id : l.source;
            const t = typeof l.target === 'object' ? l.target.id : l.target;
            deg.set(s, (deg.get(s) || 0) + 1);
            deg.set(t, (deg.get(t) || 0) + 1);
        }
        return deg;
    }, [graphData.nodes, graphData.links]);

    const selectionNodeIds = useMemo(() => {
        // When a node is selected, we only want labels for that node + its 1-hop neighbors.
        if (!selectedNode?.id) return null;
        const ids = new Set([selectedNode.id]);
        const neigh = adjacency.get(selectedNode.id);
        if (neigh) for (const id of neigh) ids.add(id);
        return ids;
    }, [selectedNode, adjacency]);

    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
    };

    const focusNode = (node) => {
        if (!node || !fgRef.current) return;
        setSelectedNode(node);

        // A gentle focus that works well on mobile.
        fgRef.current.centerAt(node.x, node.y, 800);
        fgRef.current.zoom(3, 800);
    };

    const handleSearchKeyDown = (event) => {
        if (event.key !== 'Enter') return;
        const first = searchMatches[0];
        if (first) focusNode(first);
    };

    const openNodeNote = (node) => {
        const nodeId = node.id.replace(/ /g, '_');
        navigate(`/notes/${nodeId}`);
    };

    const handleNodeClick = (node) => {
        // Mobile-friendly: first tap selects (highlights + labels). Second tap opens the note.
        const now = Date.now();
        const last = lastTapRef.current;
        const sameNode = last.nodeId === node.id;
        const withinWindow = now - last.atMs < 700;

        lastTapRef.current = { nodeId: node.id, atMs: now };

        if (sameNode && withinWindow) {
            openNodeNote(node);
            return;
        }

        focusNode(node);
    };

    const renderNode = (node, ctx, globalScale) => {
        const isHighlighted = highlightedNodeIds.has(node.id);
        const isSelected = selectedNode?.id === node.id;

        // Node dot — larger on mobile for easier tapping
        const baseR = isMobile ? 9 : 6;
        const r = isSelected ? baseR + 3 : isHighlighted ? baseR + 1.5 : baseR;

        ctx.beginPath();
        ctx.arc(node.x, node.y, r, 0, 2 * Math.PI, false);
        ctx.fillStyle = isSelected
            ? 'rgba(255, 99, 71, 0.95)'
            : isHighlighted
                ? 'rgba(52, 152, 219, 0.9)'
                : 'rgba(180, 200, 220, 0.75)';
        ctx.fill();

        // Labels
        // If a node is selected: ONLY show labels for the selected node + its connected nodes.
        // Otherwise: show labels for nodes with >3 connections (plus highlighted nodes).
        const degree = nodeDegree.get(node.id) || 0;
        const shouldLabel = showLabels && (selectionNodeIds
            ? selectionNodeIds.has(node.id)
            : (degree > 3 || isHighlighted));

        if (shouldLabel) {
            const label = node.label;
            const fontSize = Math.max(10, 14 / globalScale);
            ctx.font = `${fontSize}px Sans-Serif`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';

            // Simple collision avoidance: skip labels whose bounding box would overlap
            // a label already drawn this frame.
            const boxes = ctx.__labelBoxes || (ctx.__labelBoxes = []);
            const textWidth = ctx.measureText(label).width;
            const padX = 6;
            const padY = 4;
            const x = node.x;
            const y = node.y + 16;
            const box = {
                x1: x - textWidth / 2 - padX,
                y1: y - fontSize / 2 - padY,
                x2: x + textWidth / 2 + padX,
                y2: y + fontSize / 2 + padY,
            };

            const overlaps = boxes.some(b => !(box.x2 < b.x1 || box.x1 > b.x2 || box.y2 < b.y1 || box.y1 > b.y2));
            if (overlaps) return;
            boxes.push(box);

            // White halo for legibility
            ctx.lineWidth = 3;
            ctx.strokeStyle = 'rgba(255,255,255,0.9)';
            ctx.strokeText(label, x, y);

            ctx.fillStyle = 'rgba(0,0,0,0.9)';
            ctx.fillText(label, x, y);
        }
    };

    // labels are always on by default

    const listQuery = searchQuery.trim().toLowerCase();
    const filteredList = listQuery
        ? allNodes.filter(n => (n.label || n.id).toLowerCase().includes(listQuery))
        : allNodes;

    return (
        <div className={styles.graphContainer}>
            <Navigation />

            <div className={styles.controls}>
                <input
                    type="text"
                    placeholder={viewMode === 'graph' ? 'Search nodes (Enter to focus)…' : 'Search notes…'}
                    value={searchQuery}
                    onChange={handleSearchChange}
                    onKeyDown={handleSearchKeyDown}
                    className={styles.searchInput}
                />
                <button
                    className={styles.toggleButton}
                    onClick={() => setViewMode(v => v === 'graph' ? 'list' : 'graph')}
                >
                    {viewMode === 'graph' ? 'List view' : 'Graph view'}
                </button>
                {viewMode === 'graph' && (
                    <div className={styles.hint}>
                        Tip: tap a node to highlight; tap again to open the note.
                    </div>
                )}
            </div>

            {viewMode === 'graph' ? (
                <ForceGraph2D
                    ref={fgRef}
                    graphData={graphData}
                    nodeAutoColorBy="group"
                    onNodeClick={handleNodeClick}
                    onNodeHover={isMobile ? undefined : setHoverNode}
                    onRenderFramePre={(ctx) => {
                        ctx.__labelBoxes = [];
                    }}
                    nodeCanvasObject={renderNode}
                    nodeCanvasObjectMode={() => 'before'}
                    linkColor={() => 'rgba(120,120,120,0.35)'}
                    enableNodeDrag={!isMobile}
                    onNodeDragEnd={(node) => {
                        node.fx = null;
                        node.fy = null;
                    }}
                />
            ) : (
                <div className={styles.listView}>
                    <p className={styles.listCount}>{filteredList.length} notes</p>
                    <ul className={styles.noteList}>
                        {filteredList.map(node => (
                            <li key={node.id}>
                                <Link to={`/notes/${node.id}`} className={styles.noteLink}>
                                    {node.label || node.id}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default KnowledgeGraph;
